import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { getCurrentUser } from '../utils/auth';

const PlanContext = createContext();

/**
 * Hook to access plan features and trial status.
 *
 * Returns { features, trial, loading, hasPlanFeature }
 *   - features:        raw feature map { feeManagement: true, aiMock: false, ... }
 *   - trial:           { active: true, expiresAt, daysLeft } or null
 *   - loading:         true while the first fetch is in-flight
 *   - hasPlanFeature:  (name) => boolean — returns true if the feature is enabled
 *                      (defaults to true if no plan / loading / error — fail open)
 */
export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within PlanProvider');
  }
  return context;
};

export const PlanProvider = ({ children }) => {
  const [features, setFeatures] = useState({});
  const [planFeatures, setPlanFeatures] = useState({}); // plan-only values (ignoring institute module toggles)
  const [hiddenFeatures, setHiddenFeatures] = useState([]);
  const [trial, setTrial] = useState(null);
  const [grace, setGrace] = useState(null); // { inGrace, daysLeft, status }
  const [loading, setLoading] = useState(true);
  const [planLimitAlert, setPlanLimitAlert] = useState(null); // { message, limitField, current, max, noPlan }

  const fetchFeatures = useCallback(async () => {
    try {
      const user = getCurrentUser();
      // Only fetch for institute-level users (admin/superadmin/instructor/student)
      // Tenantadmin has no plan restrictions
      if (!user || user.usertype === 'tenantadmin') {
        setFeatures({});
        setPlanFeatures({});
        setHiddenFeatures([]);
        setTrial(null);
        setGrace(null);
        setLoading(false);
        return;
      }

      const { data } = await apiClient.get('/billing/plan-features');
      setFeatures(data?.features || {});
      setPlanFeatures(data?.planFeatures || {});
      setHiddenFeatures(data?.hiddenFeatures || []);
      setTrial(data?.trial || null);

      // Check for grace period from API response
      const trialGrace = data?.trial?.inGracePeriod
        ? { inGrace: true, daysLeft: data.trial.graceDaysRemaining, status: 'trial_expired' }
        : null;
      const subGrace = data?.subscriptionGrace?.inGracePeriod
        ? { inGrace: true, daysLeft: data.subscriptionGrace.graceDaysRemaining, status: 'subscription_expired' }
        : null;
      setGrace(trialGrace || subGrace || null);
    } catch (err) {
      console.warn('Failed to fetch plan features, showing all modules:', err.message);
      setFeatures({}); // fail open
      setPlanFeatures({});
      setHiddenFeatures([]);
      setTrial(null);
      setGrace(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // Listen for grace period events from apiClient interceptor
  useEffect(() => {
    const handleGrace = (e) => {
      setGrace(e.detail);
    };
    window.addEventListener('vc-plan-grace', handleGrace);
    return () => window.removeEventListener('vc-plan-grace', handleGrace);
  }, []);

  // Listen for plan-limit 403 events from apiClient interceptor
  useEffect(() => {
    const handleLimit = (e) => {
      setPlanLimitAlert(e.detail);
    };
    window.addEventListener('vc-plan-limit', handleLimit);
    return () => window.removeEventListener('vc-plan-limit', handleLimit);
  }, []);

  const dismissPlanLimitAlert = useCallback(() => setPlanLimitAlert(null), []);

  /**
   * Check if a feature is completely hidden (platform-level kill switch).
   * Hidden features should not appear at all — not even as locked.
   */
  const isFeatureHidden = useCallback(
    (featureName) => {
      if (!featureName) return false;
      return hiddenFeatures.includes(featureName);
    },
    [hiddenFeatures],
  );

  /**
   * Check if a specific plan feature is enabled.
   * Returns true (fail open) when:
   *   - Still loading
   *   - No features object (no plan assigned)
   *   - Feature key not present in the map
   */
  const hasPlanFeature = useCallback(
    (featureName) => {
      if (!featureName) return true;
      if (Object.keys(features).length === 0) return true; // no plan = show everything
      return features[featureName] !== false;
    },
    [features],
  );

  /**
   * Check if the plan itself allows a feature (ignoring institute module toggles).
   * Used by Settings → Modules to show "Not in plan" badge correctly.
   */
  const planAllowsFeature = useCallback(
    (featureName) => {
      if (!featureName) return true;
      if (Object.keys(planFeatures).length === 0) return true; // no plan data = assume allowed
      return planFeatures[featureName] !== false;
    },
    [planFeatures],
  );

  return (
    <PlanContext.Provider value={{ features, planFeatures, hiddenFeatures, trial, grace, loading, hasPlanFeature, planAllowsFeature, isFeatureHidden, refreshPlan: fetchFeatures, planLimitAlert, dismissPlanLimitAlert }}>
      {children}
    </PlanContext.Provider>
  );
};
